/**
 * The bar that stands in a room while a meeting runs there.
 *
 * Not a notification: the room's own state, for as long as it lasts. The
 * second line says what is actually happening — who is sharing a screen
 * matters more than a headcount, because it is the reason to go in.
 */

import { useEffect, useState } from 'react'
import type { ReactElement } from 'react'

import type { Call, UUID } from '@/api/types'
import { Avatar, Button } from '@/components/ui'
import styles from './CallWindow.module.css'

function nameOf(call: Call, userId: UUID | null | undefined): string | null {
  if (userId === null || userId === undefined) return null
  const person = call.participants.find((participant) => participant.user?.id === userId)?.user
  if (person === undefined || person === null) return null
  const display = person.display_name.trim()
  return display === '' ? person.username : display
}

function joinedPeople(call: Call): { id: UUID; name: string }[] {
  return call.participants
    .filter((participant) => participant.status === 'joined' && participant.user !== null)
    .map((participant) => ({
      id: participant.user?.id ?? '',
      name:
        participant.user?.display_name.trim() === ''
          ? (participant.user?.username ?? 'Someone')
          : (participant.user?.display_name ?? 'Someone'),
    }))
}

export function CallBar({
  call,
  joined,
  onJoin,
}: {
  call: Call
  /** We are already in this one; the bar says so rather than offering Join. */
  joined: boolean
  onJoin: () => void
}): ReactElement {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  const seconds = Math.max(0, Math.floor((now - new Date(call.started_at).getTime()) / 1000))
  const minutes = Math.floor(seconds / 60)
  const clock = `${minutes}:${String(seconds % 60).padStart(2, '0')}`

  const people = joinedPeople(call)
  const sharer = nameOf(call, call.sharing_user_id)
  // Alone in it, the starter is the news: one name reads as a list of one.
  const startedAlone = people.length === 1 && people[0]?.id === call.initiator?.id
  const names =
    people.length === 0
      ? 'Nobody has joined yet'
      : startedAlone
        ? `${people[0]?.name ?? 'Someone'} started it · 1 in the call`
        : people.length <= 3
        ? people.map((person) => person.name).join(', ')
        : `${people[0]?.name ?? 'Someone'} and ${people.length - 1} more`

  return (
    <div className={styles.roomBar} role="status">
      <span className={styles.statusDot} />
      <div className={styles.roomBarText}>
        <span className={styles.roomBarTitle}>
          {call.media === 'video' ? 'Video meeting' : 'Audio call'} · {clock}
        </span>
        {/* The sharer replaces the name list: it is the reason to go in. */}
        <span className={styles.roomBarMeta}>
          {sharer === null ? names : `${sharer} is sharing · ${people.length} in the call`}
        </span>
      </div>

      <div className={styles.roomBarFaces}>
        {people.slice(0, 3).map((person) => (
          <Avatar key={person.id} name={person.name} id={person.id} size="xs" decorative />
        ))}
      </div>

      {joined ? null : (
        <Button size="sm" variant="primary" onClick={onJoin}>
          Join
        </Button>
      )}
    </div>
  )
}
